import { appLocales, Locales } from "@/lib/i18n";
import { segments } from "@/locales/segment";

export type ProductRevalidationTarget = {
  path: string;
  tag: string;
};

type RevalidationDependencies = {
  revalidatePath: (path: string) => void;
  revalidateTag: (tag: string) => void;
};

type PrismicFetchOptions = Record<string, unknown> & {
  next?: Record<string, unknown> & {
    tags?: string[];
  };
};

export const getProductCacheTag = (locale: Locales, uid: string) => `prismic:product:${locale}:${uid}`;

export const withPrismicProductCacheTag = (
  fetchOptions: PrismicFetchOptions | undefined,
  locale: Locales,
  uid: string
): PrismicFetchOptions => {
  if (fetchOptions?.cache === "no-store" || fetchOptions?.next?.revalidate === 0) {
    return fetchOptions;
  }

  const tag = getProductCacheTag(locale, uid);
  const existingTags = fetchOptions?.next?.tags || [];

  return {
    ...fetchOptions,
    next: {
      ...fetchOptions?.next,
      tags: Array.from(new Set([...existingTags, tag])),
    },
  };
};

export const getProductRevalidationTarget = (locale: Locales, uid: string): ProductRevalidationTarget => {
  const prefix = locale === appLocales.defaultLocale ? "" : `/${locale}`;

  return {
    path: `${prefix}/${segments[locale].product}/${uid}`,
    tag: getProductCacheTag(locale, uid),
  };
};

export const applyProductRevalidationTargets = (
  targets: Iterable<ProductRevalidationTarget>,
  dependencies: RevalidationDependencies
) => {
  for (const target of targets) {
    dependencies.revalidateTag(target.tag);
    dependencies.revalidatePath(target.path);
  }
};
